import dynamic from 'next/dynamic';
import Router, { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';

import { ProductAffiliation } from '@common/type/ProductType';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Grid, Typography, IconButton } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { scroller, Element } from 'react-scroll';
import bundleAPI from 'src/api/bundle';
import cpdAPI from 'src/api/cpd';
import userAPI from 'src/api/user';
import config from 'src/config';
import { reloadAfterMergedAccount } from 'src/helpers/auth';
import { CpdEmailValidateResult, EnrollmentStatus, ROUTES } from 'src/helpers/constants';
import { useCpdBundleEnrollment, useProfile } from 'src/helpers/use';
import { checkCpdEmail, isEmptyObject } from 'src/helpers/utils';
import { IBreadcrumb } from 'src/interfaces';
import { ITapScroll } from 'src/interfaces/common';
import { IEnrollmentsBundle, IProductDetail } from 'src/interfaces/product';
import { IUserIDSProfile } from 'src/interfaces/user';
import { LearningTypeId } from 'src/types/product';
import useSWR from 'swr';
import Cookies from 'universal-cookie';

import ProductDetailStyle, { LoadingContainer } from './style';

import Button from '@components/atoms/button';
import { useDialog } from '@components/atoms/dialog';
import FontAwesomeIcon from '@components/atoms/fontAwesomeIcon';
import { LoadingInfiniteScroll } from '@components/atoms/loading';
import ProductCPDEnrollment from '@components/molecules/cpdEnrollment/product';
import FooterBundleDetail from '@components/molecules/footerProductDetail';
import Heading from '@components/molecules/heading/product/detail';
import { ProductDetailContainer, PurchaseButton } from '@components/molecules/productDetail/style';
import CpdPreparationInformationSection from '@components/organisms/cpd/cpdPreparationInformationSection';
import ProductCPD from '@components/organisms/cpd/productCpd';
import ProductDetailCourses from '@components/organisms/productDetailCourses';
import ProductDetailDefaultGlance from '@components/organisms/productDetailDefaultGlance';
import ProductDetailDescription from '@components/organisms/productDetailDescription';
import ProductDetailPaymentMethod, {
  IProductDetailPaymentMethod,
} from '@components/organisms/productDetailPaymentMethods';
import ProductDetailTabScroll from '@components/organisms/productDetailTabScroll';
import { sendPageViewContentCourseDetail } from '@helpers/googleTagManager';

const FooterCpdDetail = dynamic(import('@components/organisms/cpd/footerCourseDetail'));
const LoginModal = dynamic(import('src/components/atoms/dialog/LoginModal'));
const CitizenIdModal = dynamic(import('@components/atoms/dialog/CitizenIdModal'));
const CitizenIdDuplicateModal = dynamic(import('@components/atoms/dialog/CitizenIdDuplicateModal'));
const CitizenIdConnectOtherAccountModal = dynamic(import('@components/atoms/dialog/CitizenIdConnectOtherAccountModal'));
const CannotEnrollmentToCourseModal = dynamic(import('@components/atoms/dialog/CannotEnrollmentToCourseModal'));
const ConfirmEmailModal = dynamic(import('src/components/atoms/dialog/confirmEmail'));
const ConfirmEnrollMixBundleModal = dynamic(import('src/components/atoms/dialog/confirmEnrollMixBundleModal'));
const CPDEnrollment = dynamic(import('src/components/molecules/cpdEnrollment'));

const CPDBundleDetail = () => {
  const cookies = new Cookies();

  // useRef
  const targetRef = useRef<any>();
  const isSendEventGtm = useRef<boolean>(false);

  const router = useRouter();

  const loginDialog = useDialog();
  const citizenIdDialog = useDialog();
  const citizenIdDuplicateDialog = useDialog();
  const citizenIdConnectOtherAccountModal = useDialog();
  const cannotEnrollToCourseDialog = useDialog();
  const emailErrorDialog = useDialog();
  const confirmEmailDialog = useDialog();
  const confirmEnrollMixBundle = useDialog();

  // useState
  const [initEnrollmentStep, setInitEnrollmentStep] = useState(1);
  const [citizenId, setCitizenId] = useState<string>('');
  const [newCitizenId, setNewCitizenId] = useState<string>('');
  const [isNewCitizenId, setIsNewCitizenId] = useState<boolean>(false);
  const [sectionIndex, setSectionIndex] = useState(0);
  const [breadcrumbList, setBreadcrumList] = useState<IBreadcrumb[]>([]);
  const [enrollmentedBundleSkuIds, setEnrollmentedBundleSkuIds] = useState<number[]>([]);
  const [canTrain, setCanTrain] = useState<boolean>(true);
  const [isOpenPersonalInformationForm, setIsOpenPersonalInformationForm] = useState<boolean>(false);
  const [loadingEnrollment, setLoadingEnrollment] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState<boolean>(false);
  const [validatedEmail, setValidatedEmail] = useState(false);
  const [isEmailNotPass, setIsEmailNotPass] = useState(false);
  const [initEnrollmentData, setInitEnrollmentData] = useState(null);

  // common variable
  const { t } = useTranslation('common');
  const { data: bundle } = useSWR<IProductDetail>('CPD_BUNDLE');
  const { profile, isLoading: isProfileLoading, mutate } = useProfile();

  const { cpdBundleEnrollment, isLoading: isCpdEnrollmentLoading } = useCpdBundleEnrollment(
    citizenId,
    bundle?.id?.toString(),
    bundle?.sale_price,
  );

  const getCpdBundleEnrollmentStatus = (): EnrollmentStatus => {
    if (!bundle?.isEnabled) return EnrollmentStatus.UNAVAILABLE;
    if (cpdBundleEnrollment?.canEnrollToBundle) return EnrollmentStatus.AVAILABLE;
    return EnrollmentStatus[cpdBundleEnrollment?.enrollStatus || EnrollmentStatus.AVAILABLE];
  };

  const productDetailPaymentMethods: IProductDetailPaymentMethod[] = [
    {
      imageUrl: `${config.IMAGE_CDN_HOST}/assets/product-detail/b2c_how_to_start_buying_course.jpg`,
      label: t('product_detail.bundle.payment_method.cpd_collect_hour'),
    },
    {
      imageUrl: `${config.IMAGE_CDN_HOST}/assets/product-detail/b2c_how_to_start_buying_course_credit.jpg`,
      label: t('product_detail.bundle.payment_method.credit'),
    },
    {
      imageUrl: `${config.IMAGE_CDN_HOST}/assets/product-detail/b2c_how_to_start_buying_course_qr.jpg`,
      label: t('product_detail.bundle.payment_method.qr_code'),
    },
    {
      imageUrl: `${config.IMAGE_CDN_HOST}/assets/product-detail/b2c_how_to_start_buying_course_paypal.jpg`,
      label: t('product_detail.bundle.payment_method.paypal'),
    },
  ];

  const tabMenuList: ITapScroll[] = [
    {
      text: t('product_detail.bundle.tab_menu.bundle_detail'),
      elementName: 'bundleDetail',
    },
    {
      text: t('product_detail.bundle.tab_menu.courses', {
        courseAmount: bundle?.product_bundle_items ? bundle?.product_bundle_items.length : 0,
      }),
      elementName: 'courses',
    },
    {
      text: t('cpd_course.tab_menu.prepare_train'),
      elementName: 'cpdPreTrain',
    },
    {
      text: t('product_detail.bundle.tab_menu.payment'),
      elementName: 'payment',
    },
  ];

  const scrollToComponent = (value: string) => {
    const position = () => {
      if (window.pageYOffset < targetRef?.current?.offsetHeight + 50) {
        return -150;
      }
      return -60;
    };
    scroller.scrollTo(value, {
      duration: 1000,
      smooth: true,
      offset: position(),
    });
  };

  const handleChange = (event: any, newValue: number) => {
    setSectionIndex(newValue);
    switch (newValue) {
      case 0:
        scrollToComponent(tabMenuList[0].elementName);
        break;
      case 1:
        scrollToComponent(tabMenuList[1].elementName);
        break;
      case 2:
        scrollToComponent(tabMenuList[2].elementName);
        break;
      case 3:
        scrollToComponent(tabMenuList[3].elementName);
        break;
      default:
        break;
    }
  };

  const gotoSystemAdminsEditBundle = () => {
    Router.push(`/system_admins/course_bundles/${bundle?.id_name}/edit`);
  };

  const editButton = profile?.roles.some((role) => role.name === 'content_editor') ? (
    <Button variant="outlined" className="edit-button" onClick={() => gotoSystemAdminsEditBundle()}>
      <div>
        <FontAwesomeIcon icon="fa-light fa-pen" style={{ fontSize: '15px' }} />
      </div>
      <div>{t('product_detail.edit_button')}</div>
    </Button>
  ) : (
    <></>
  );

  const setEnrollmentsBundleSkuIdsData = async () => {
    if (!bundle) return;
    const response = await bundleAPI.fetchEnrollmentBundleByIdOrIdName(bundle?.id_name);
    const { data } = response;
    setEnrollmentedBundleSkuIds(
      data?.filter((item: IEnrollmentsBundle) => item.is_enroll).map((item: IEnrollmentsBundle) => item.product_sku_id),
    );
  };

  const productCPDEnrollment = (
    <ProductCPDEnrollment
      cpdBundleEnrollment={cpdBundleEnrollment!}
      open={isOpenPersonalInformationForm}
      onClose={() => setIsOpenPersonalInformationForm(false)}
      productDetail={bundle!}
      isEmailValid={isEmailValid}
      initEnrollmentData={initEnrollmentData}
      userId={profile?.id}
    />
  );

  const renderDialogAndModal = () => <>{productCPDEnrollment}</>;

  const checkEmailValid = async (): Promise<CpdEmailValidateResult> => {
    if (!profile) return CpdEmailValidateResult.NotPass;

    const firstResult = checkCpdEmail(profile.email_validated_result_code);
    if (firstResult === CpdEmailValidateResult.Pass) {
      return CpdEmailValidateResult.Pass;
    }
    const result = await userAPI.validateEmail();
    const secondResult = checkCpdEmail(result.data.code);
    return secondResult;
  };

  const handleCheckEmail = async () => {
    setLoadingEnrollment(true);
    // check email
    if (!validatedEmail) {
      const result = await checkEmailValid();
      setValidatedEmail(true);
      if (result === CpdEmailValidateResult.Pass) {
        setIsEmailValid(true);
      } else if (result === CpdEmailValidateResult.Uncertain) {
        setIsEmailValid(false);
      } else {
        setIsEmailNotPass(true);
        emailErrorDialog.open();
        setLoadingEnrollment(false);
        return true;
      }
    }

    setLoadingEnrollment(false);
    return false;
  };

  const handleSubmitCitizenIdForm = async (value: string) => {
    const res = await userAPI.checkCitizenIdDuplicate(value);
    const userIDS: IUserIDSProfile = res.data;
    if (userIDS && profile?.email !== userIDS.email) {
      if (!userIDS.email) {
        setNewCitizenId(value);
        citizenIdDuplicateDialog.open();
      } else {
        citizenIdConnectOtherAccountModal.open();
      }
      return;
    }
    setIsNewCitizenId(true);
    setCitizenId(value);
  };

  const checkCourseRegularIsEnrolled = () => {
    const courseNonCpd = bundle?.product_bundle_items.filter(
      (item) => item.product_skus.products.learning_type_id !== LearningTypeId.CPD,
    );
    return (
      courseNonCpd?.some((regularCourse) =>
        enrollmentedBundleSkuIds.some((cpdCourse) => regularCourse.product_sku_id === cpdCourse),
      ) || false
    );
  };

  const getGa4Items = () => {
    return [
      {
        item_id: bundle?.productSku || '',
        item_name: bundle?.name || '',
        affiliation: bundle?.productAffiliation || ProductAffiliation.SKILL_LANE,
        coupon: null,
        discount: null,
        index: null,
        item_brand: bundle?.gtmProductBrand || null,
        item_category: bundle?.gtmProductBrand || null,
        item_category2: bundle?.gtmProductBrand || null,
        item_category3: null,
        item_category4: null,
        item_category5: null,
        item_list_id: null,
        item_list_name: null,
        item_variant: bundle?.gtmContentId || null,
        location_id: null,
        price: bundle?.sale_price || 0,
        quantity: 1,
      },
    ];
  };

  const handleChooseEnrollClick = async () => {
    if (!profile) {
      loginDialog.open();
      return;
    }
    if (profile.email.includes(profile.uid)) {
      confirmEmailDialog.open();
      return;
    }
    const isEmailError = await handleCheckEmail();

    if ((validatedEmail && isEmailNotPass) || isEmailError) {
      emailErrorDialog.open();
      return;
    }

    if (!citizenId) {
      citizenIdDialog.open();
      return;
    }

    if (checkCourseRegularIsEnrolled() && !confirmEnrollMixBundle.opened) {
      return confirmEnrollMixBundle.open();
    }

    // go to step 1
    handleOpenStepOne();
  };

  const handleConfirmEnrollBundle = () => {
    handleChooseEnrollClick();
  };

  const handleOpenStepOne = () => {
    confirmEnrollMixBundle.close();
    if (canTrain && cpdBundleEnrollment && !!citizenId) {
      return setIsOpenPersonalInformationForm(true);
    }
  };

  const handleInitEnrollmentStep = async () => {
    const enrollmentCitizenId = cookies.get('cpd_enrollment_citizenId');
    const enrollmentStep = +cookies.get('cpd_enrollment_step'); // Step is 1, 2, 3, 4
    if (enrollmentStep) {
      await handleCheckEmail();
      setValidatedEmail(true);
      setInitEnrollmentStep(enrollmentStep);
      if (enrollmentStep === 2 || enrollmentStep === 3) {
        const resp = await cpdAPI.fetchTemporaryCpdEnrollmentFormData(bundle?.id!, profile?.id!);
        if (resp.data) {
          setInitEnrollmentData(resp.data);
        }
      }
      if (enrollmentCitizenId) {
        setCitizenId(enrollmentCitizenId);
        setIsNewCitizenId(true);
      }
    }
  };

  const cpdEnrollmentStatus = getCpdBundleEnrollmentStatus();
  const isLoading = isProfileLoading || isCpdEnrollmentLoading;

  useEffect(() => {
    if (citizenId && citizenId === profile?.cpd_citizen_id) return;
    if (profile) handleInitEnrollmentStep();
    if (!profile?.cpd_citizen_id) {
      return;
    }
    setCitizenId(profile.cpd_citizen_id);
  });

  useEffect(() => {
    if (!bundle) return;

    setEnrollmentsBundleSkuIdsData();
    setBreadcrumList([
      {
        description: t('product_detail.breadcrum.homepage'),
        url: ROUTES.HOME,
      },
      {
        description: bundle.name,
      },
    ]);
  }, [bundle]);

  useEffect(() => {
    if (!cpdBundleEnrollment || isCpdEnrollmentLoading) return;

    const { canEnrollToBundle, enrollStatus } = cpdBundleEnrollment;
    const isRegulatorUnChecked = enrollStatus === EnrollmentStatus.REGULATOR_UNCHECKED;
    const haveCitizenId = !!citizenId;

    if (!isNewCitizenId) {
      setCanTrain(canEnrollToBundle);
      return;
    }

    if (canEnrollToBundle && (haveCitizenId || isRegulatorUnChecked)) {
      cookies.remove('cpd_enrollment_step', { path: '/' });
      cookies.remove('cpd_enrollment_citizenId', { path: '/' });
      if (checkCourseRegularIsEnrolled() && !confirmEnrollMixBundle.opened) {
        confirmEnrollMixBundle.open();
      } else {
        handleOpenStepOne();
      }
      return;
    }

    if (!canEnrollToBundle && haveCitizenId) {
      cannotEnrollToCourseDialog.open();
    }
  }, [cpdBundleEnrollment, isNewCitizenId]);

  useEffect(() => {
    const isAvailable = [EnrollmentStatus.AVAILABLE, EnrollmentStatus.FREE_COURSE].includes(
      getCpdBundleEnrollmentStatus(),
    );
    if (!isAvailable || isCpdEnrollmentLoading || isLoading || isSendEventGtm.current) return;

    isSendEventGtm.current = true;

    sendPageViewContentCourseDetail({
      value: bundle?.sale_price || 0,
      currency: 'THB',
      items: getGa4Items(),
    });
  }, [bundle, cpdEnrollmentStatus, cpdBundleEnrollment]);

  useEffect(() => {
    reloadAfterMergedAccount();
  }, []);

  return (
    <>
      {renderDialogAndModal()}
      {emailErrorDialog.render(
        <Box>
          <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
            <IconButton onClick={emailErrorDialog.close} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          <Box display="flex" alignItems="center" flexDirection="column" rowGap="1">
            <FontAwesomeIcon style={{ fontSize: '50px' }} color="red" icon="fa-light fa-circle-exclamation" />
            <Typography variant="h4" align="center" sx={{ mt: 2 }}>
              {t('alert.email_error')}
            </Typography>
            <Button sx={{ m: 2, width: '120px' }} variant="contained" color="primary" onClick={emailErrorDialog.close}>
              {t('all_course.filter.submit')}
            </Button>
          </Box>
        </Box>,
      )}
      {loginDialog.render(<LoginModal dialog={loginDialog} redirectUrl={`${config.APP_DOMAIN}/${router.asPath}`} />)}
      {citizenIdDialog.render(<CitizenIdModal dialog={citizenIdDialog} onSubmit={handleSubmitCitizenIdForm} />)}
      {cannotEnrollToCourseDialog.render(
        <CannotEnrollmentToCourseModal
          dialog={cannotEnrollToCourseDialog}
          citizenId={citizenId}
          regulator={bundle?.regulator}
          cpdEnrollment={cpdBundleEnrollment}
        />,
      )}
      {citizenIdDuplicateDialog.render(
        <CitizenIdDuplicateModal
          dialog={citizenIdDuplicateDialog}
          email={profile?.email!}
          citizenId={newCitizenId}
          setCitizenId={setNewCitizenId}
        />,
      )}
      {citizenIdConnectOtherAccountModal.render(
        <CitizenIdConnectOtherAccountModal dialog={citizenIdConnectOtherAccountModal} />,
      )}
      {confirmEmailDialog.render(<ConfirmEmailModal citizenId={citizenId} dialog={confirmEmailDialog} />)}
      {confirmEnrollMixBundle.render(
        <ConfirmEnrollMixBundleModal
          dialog={confirmEnrollMixBundle}
          handleConfirmEnrollBundle={handleConfirmEnrollBundle}
        />,
      )}
      {bundle && !isEmptyObject(bundle) ? (
        <>
          <Box ref={targetRef}>
            <Heading
              originalPrice={bundle.original_price}
              salePrice={bundle.sale_price}
              breadcrumbList={breadcrumbList}
              highlight_medium_image={bundle.header_card_image ? bundle.header_card_image : ''}
              name={bundle.name}
              editButton={editButton}
              isLoading={isLoading}
              cpdEnrollmentStatus={cpdEnrollmentStatus}
              courseAmount={bundle?.product_bundle_items?.length || 0}
              onChooseEnrollClick={handleChooseEnrollClick}
            />
          </Box>

          <ProductDetailStyle>
            <div className="tab-nav" style={{ backgroundColor: '#F5F5F5' }}>
              <ProductDetailTabScroll
                headingHeight={targetRef?.current?.offsetHeight}
                value={sectionIndex}
                onChange={handleChange}
                tabScrollTextList={tabMenuList.map((value) => value.text)}
              />
            </div>
            <Box className="container" sx={{ marginTop: '30px' }}>
              <div>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={12} lg={8} order={{ lg: 1, xs: 2 }}>
                    <ProductDetailDescription description={bundle.description} collapseSize={450} />
                  </Grid>
                  <Grid item xs={12} md={12} lg={4} order={{ lg: 2, xs: 1 }}>
                    <Element name={tabMenuList[0].elementName} />
                    <ProductDetailDefaultGlance defaultGlances={bundle.default_glances} />
                  </Grid>
                  <Grid item xs={12} md={12} lg={12} order={{ lg: 3, xs: 3 }}>
                    <Element name={tabMenuList[1].elementName} />
                    <ProductDetailCourses
                      productBundleItems={bundle?.product_bundle_items}
                      enrollmentedBundleSkuIds={enrollmentedBundleSkuIds}
                      bundleName={bundle?.name}
                    />
                  </Grid>
                </Grid>
              </div>
            </Box>
          </ProductDetailStyle>
          <div>
            <Element name={tabMenuList[2].elementName} />
            <CpdPreparationInformationSection />
          </div>
          <div>
            <Element name={tabMenuList[3].elementName} />
            <ProductDetailPaymentMethod productDetailPaymentMethods={productDetailPaymentMethods} />
          </div>

          <ProductDetailContainer>
            <Box my={{ xs: '24px', lg: '48px' }}>
              <ProductCPD />
            </Box>
          </ProductDetailContainer>
          <FooterBundleDetail
            productDetail={bundle!}
            isLoading={isLoading}
            height={targetRef?.current?.offsetHeight}
            cpdEnrollmentStatus={cpdEnrollmentStatus}
            onChooseEnrollClick={handleChooseEnrollClick}
            courseAmount={bundle?.product_bundle_items?.length || 0}
          />
        </>
      ) : (
        <LoadingContainer>
          <LoadingInfiniteScroll />
        </LoadingContainer>
      )}
    </>
  );
};

export default CPDBundleDetail;
